"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildDepTreeFromFiles = exports.buildDepGraphFromFiles = void 0;
const fs = require("fs");
const path = require("path");
const csProjParser = require("./parsers/csproj-parser");
const debugModule = require("debug");
const depsParser = require("dotnet-deps-parser");
const dotnetCoreParser = require("./parsers/dotnet-core-parser");
const dotnetCoreV2Parser = require("./parsers/dotnet-core-v2-parser");
const dotnetFrameworkParser = require("./parsers/dotnet-framework-parser");
const projectJsonParser = require("./parsers/project-json-parser");
const packagesConfigParser = require("./parsers/packages-config-parser");
const errors_1 = require("../errors");
const types_1 = require("./types");
const dotnet = require("./cli/dotnet");
const nugetFrameworksParser = require("./csharp/nugetframeworks_parser");
const runtimeAssembly = require("./runtime-assembly");
const debug = debugModule('snyk');
const PARSERS = {
    'dotnet-core': {
        depParser: dotnetCoreParser,
        fileContentParser: JSON,
    },
    'dotnet-core-v2': {
        depParser: dotnetCoreV2Parser,
        fileContentParser: JSON,
    },
    'packages.config': {
        depParser: dotnetFrameworkParser,
        fileContentParser: packagesConfigParser,
    },
    'project.json': {
        depParser: dotnetFrameworkParser,
        fileContentParser: projectJsonParser,
    },
};
function getPackagesFolder(packagesFolder, projectRootFolder) {
    if (packagesFolder) {
        return path.resolve(process.cwd(), packagesFolder);
    }
    return path.resolve(projectRootFolder, 'packages');
}
function getRootName(root, projectRootFolder, projectNamePrefix) {
    const defaultRootName = path.basename(root || projectRootFolder || '');
    if (projectNamePrefix) {
        return projectNamePrefix + defaultRootName;
    }
    return defaultRootName;
}
function getFileContents(fileContentPath) {
    try {
        debug(`Parsing content of ${fileContentPath}`);
        return fs.readFileSync(fileContentPath, 'utf-8');
    }
    catch (error) {
        throw new errors_1.FileNotProcessableError(error);
    }
}
async function buildDepGraphFromFiles(root, targetFile, manifestType, useProjectNameFromAssetsFile, projectNamePrefix, targetFramework) {
    var _a, _b, _c;
    const safeRoot = root || '.';
    const safeTargetFile = targetFile || '.';
    const fileContentPath = path.resolve(safeRoot, safeTargetFile);
    const fileContent = getFileContents(fileContentPath);
    const projectRootFolder = path.resolve(fileContentPath, '../../');
    const parser = PARSERS['dotnet-core-v2'];
    const projectAssets = await parser.fileContentParser.parse(fileContent);
    if (!((_a = projectAssets.project) === null || _a === void 0 ? void 0 : _a.frameworks)) {
        throw new errors_1.FileNotProcessableError(`unable to detect any target framework in manifest file ${safeTargetFile}, a valid one is needed to continue down this path.`);
    }
    // Scan all 'frameworks' detected in the project.assets.json file, and use the targetAlias if detected and
    // otherwise the raw key name, as it's not guaranteed that all framework objects contains a targetAlias.
    const targetFrameworks = Object.entries(projectAssets.project.frameworks).map(([key, value]) => ('targetAlias' in value ? value.targetAlias : key));
    if (targetFrameworks.length <= 0) {
        throw new errors_1.FileNotProcessableError(`unable to detect a target framework in ${projectRootFolder}, a valid one is needed to continue down this path.`);
    }
    if (targetFramework && !targetFrameworks.includes(targetFramework)) {
        console.log(`\x1b[33m⚠ WARNING\x1b[0m: Supplied targetframework \x1b[1m${targetFramework}\x1b[0m was not detected in the supplied 
manifest file. Available targetFrameworks detected was \x1b[1m${targetFrameworks.join(',')}\x1b[0m. 
Will attempt to build dependency graph anyway, but the operation might fail.`);
    }
    let resolvedProjectName = getRootName(root, projectRootFolder, projectNamePrefix);
    const projectNameFromManifestFile = (_c = (_b = projectAssets === null || projectAssets === void 0 ? void 0 : projectAssets.project) === null || _b === void 0 ? void 0 : _b.restore) === null || _c === void 0 ? void 0 : _c.projectName;
    if (manifestType === types_1.ManifestType.DOTNET_CORE &&
        useProjectNameFromAssetsFile) {
        if (projectNameFromManifestFile) {
            resolvedProjectName = projectNameFromManifestFile;
        }
        else {
            debug(`project.assets.json file doesn't contain a value for 'projectName'. Using default value: ${resolvedProjectName}`);
        }
    }
    // If a specific targetFramework has been requested, only query that, otherwise try to do them all
    const decidedTargetFrameworks = targetFramework
        ? [targetFramework]
        : targetFrameworks.filter((framework) => {
            if (!depsParser.isSupportedByV2GraphGeneration(framework)) {
                console.log(`\x1b[33m⚠ WARNING\x1b[0m: The runtime resolution flag is currently only supported for the following TargetFrameworks: .NET versions 5 and higher, all versions of .NET Core and all versions of .NET Standard. Detected a TargetFramework: \x1b[1m${framework}\x1b[0m, which will be skipped.`);
                return false;
            }
            return true;
        });
    if (decidedTargetFrameworks.length == 0) {
        throw new errors_1.InvalidManifestError(`Was not able to find any supported TargetFrameworks to scan, aborting`);
    }
    const results = [];
    for (const decidedTargetFramework of decidedTargetFrameworks) {
        // Ensure `dotnet` is installed on the system or fail trying.
        await dotnet.validate();
        // Run `dotnet publish` to create a self-contained publishable binary with included .dlls for assembly version inspection.
        const publishDir = await dotnet.publish(projectRootFolder, decidedTargetFramework);
        // Then inspect the dependency graph for the runtimepackage's assembly versions.
        const depsFilePath = path.resolve(publishDir, `${projectNameFromManifestFile}.deps.json`);
        const depsFile = fs.readFileSync(depsFilePath);
        const publishedProjectDeps = JSON.parse(depsFile.toString('utf-8'));
        const assemblyVersions = runtimeAssembly.generateRuntimeAssemblies(publishedProjectDeps);
        // Parse the TargetFramework using Nuget.Frameworks itself, instead of trying to reinvent the wheel, thus ensuring
        // we have maximum context to use later when building the depGraph.
        const location = nugetFrameworksParser.generate();
        await dotnet.restore(location);
        const response = await dotnet.run(location, [decidedTargetFramework]);
        const targetFrameworkInfo = JSON.parse(response);
        if (targetFrameworkInfo.IsUnsupported) {
            throw new errors_1.InvalidManifestError(`dotnet was not able to parse the target framework ${decidedTargetFramework}, it was reported unsupported by the dotnet runtime`);
        }
        const depGraph = parser.depParser.parse(resolvedProjectName, projectAssets, publishedProjectDeps, assemblyVersions);
        results.push({
            dependencyGraph: depGraph,
            targetFramework: decidedTargetFramework,
        });
    }
    return results;
}
exports.buildDepGraphFromFiles = buildDepGraphFromFiles;
async function buildDepTreeFromFiles(root, targetFile, packagesFolderPath, manifestType, useProjectNameFromAssetsFile, projectNamePrefix) {
    var _a, _b;
    const safeRoot = root || '.';
    const safeTargetFile = targetFile || '.';
    const fileContentPath = path.resolve(safeRoot, safeTargetFile);
    const fileContent = getFileContents(fileContentPath);
    const projectRootFolder = path.resolve(fileContentPath, '../../');
    const packagesFolder = getPackagesFolder(packagesFolderPath, projectRootFolder);
    const tree = {
        dependencies: {},
        meta: {},
        name: getRootName(root, projectRootFolder, projectNamePrefix),
        packageFormatVersion: 'nuget:0.0.0',
        version: '0.0.0',
    };
    let targetFrameworks;
    try {
        if (manifestType === types_1.ManifestType.DOTNET_CORE) {
            targetFrameworks =
                csProjParser.getTargetFrameworksFromProjFile(projectRootFolder);
        }
        else {
            // .csproj is in the same directory as packages.config or project.json
            const fileContentParentDirectory = path.resolve(fileContentPath, '../');
            targetFrameworks = csProjParser.getTargetFrameworksFromProjFile(fileContentParentDirectory);
            // finally, for the .NETFramework project, try to assume the framework using dotnet-deps-parser
            if (targetFrameworks.length <= 0) {
                // currently only process packages.config files
                if (manifestType === types_1.ManifestType.PACKAGES_CONFIG) {
                    const minimumTargetFramework = await packagesConfigParser.getMinimumTargetFramework(fileContent);
                    if (minimumTargetFramework) {
                        targetFrameworks = [minimumTargetFramework];
                    }
                }
            }
        }
    }
    catch (error) {
        return Promise.reject(error);
    }
    // Only supports the first targetFramework we find.
    // Use the newer `buildDepGraphFromFiles` for better support for multiple target frameworks.
    const targetFramework = targetFrameworks.length > 0 ? targetFrameworks[0].original : undefined;
    tree.meta = {
        targetFramework: targetFramework,
    };
    const parser = PARSERS[manifestType];
    const manifest = await parser.fileContentParser.parse(fileContent, tree);
    if (manifestType === types_1.ManifestType.DOTNET_CORE &&
        useProjectNameFromAssetsFile) {
        const projectName = (_b = (_a = manifest === null || manifest === void 0 ? void 0 : manifest.project) === null || _a === void 0 ? void 0 : _a.restore) === null || _b === void 0 ? void 0 : _b.projectName;
        if (projectName) {
            tree.name = projectName;
        }
        else {
            debug("project.assets.json file doesn't contain a value for 'projectName'. Using default value: " +
                tree.name);
        }
    }
    return parser.depParser.parse(tree, manifest, targetFramework, packagesFolder);
}
exports.buildDepTreeFromFiles = buildDepTreeFromFiles;
//# sourceMappingURL=index.js.map